import { useEffect, useMemo, useRef, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import {
  Archive,
  ArchiveRestore,
  Boxes,
  ChevronDown,
  ChevronRight,
  Check,
  CheckSquare,
  FolderInput,
  FolderKanban,
  History,
  Layers,
  ImagePlus,
  Loader2,
  MapPin,
  Maximize2,
  Music,
  Pencil,
  Plus,
  RefreshCw,
  Shirt,
  Sparkles,
  Square,
  User,
  Wand2,
  X,
} from "lucide-react";
import {
  ApiError,
  apiFetch,
  archiveProductionAsset,
  attachGeneratedReference,
  createProductionAsset,
  elementGenerationsPath,
  moveProductionAssets,
  productionAssetsPath,
  startElementGeneration,
  swrFetcher,
  unarchiveProductionAsset,
  updateProductionAsset,
  uploadAsset,
  type CapabilityInfo,
  type ElementGenerationInfo,
  type ModelInfo,
  type ProductionAssetInfo,
  type ProductionAssetType,
  type ProjectInfo,
  type ReferenceAssetInfo,
} from "@/lib/api";
import { MoveToProjectDialog } from "@/components/MoveToProjectDialog";
import { MoveResultBar } from "@/components/MoveResultBar";
import { SelectionToolbar } from "@/components/SelectionToolbar";
import { useProjectMove } from "@/hooks/useProjectMove";
import { projectName, sharedProjectId } from "@/lib/projectMove";
import { toggleId } from "@/lib/selection";
import { useProjectStore } from "@/store/project";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog } from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Tabs } from "@/components/ui/tabs";
import { MediaLightbox } from "@/components/MediaLightbox";
import { useAssetUrl } from "@/hooks/useAssetUrl";
import {
  CANDIDATE_COUNT,
  buildPortraitPlan,
  mergeAttachedReference,
  type PortraitPlan,
} from "@/lib/elementPortraits";
import { cn } from "@/lib/utils";
import { formatUsd } from "@/lib/format";
import { isPendingState } from "@/lib/api";
import { EmptyState } from "@/components/ui/empty-state";
import { AssetThumb } from "@/components/media/AssetThumb";

/** Prefer the lighter thumbnail object, fall back to the full asset. */
const refKey = (r: ReferenceAssetInfo) => r.thumbnail_storage_key ?? r.storage_key;

/** Reference thumbnail backed by an auth'd, short-lived signed URL (media tags can't send Bearer). */
function RefThumb({ r }: { r: ReferenceAssetInfo }) {
  return <AssetThumb storageKey={refKey(r)} fallback={<span className="h-full w-full animate-pulse bg-studio-panel" />} />;
}

const TYPE_TABS: { value: ProductionAssetType; label: string; icon: typeof User }[] = [
  { value: "character", label: "Characters", icon: User },
  { value: "location", label: "Locations", icon: MapPin },
  { value: "prop", label: "Props", icon: Boxes },
  { value: "wardrobe", label: "Wardrobe", icon: Shirt },
];

const TYPE_META: Record<ProductionAssetType, { label: string; icon: typeof User }> = {
  character: { label: "Character", icon: User },
  location: { label: "Location", icon: MapPin },
  prop: { label: "Prop", icon: Boxes },
  wardrobe: { label: "Wardrobe", icon: Shirt },
};

/** A reference held in dialog working state: either freshly uploaded or pre-existing. */
interface WorkingRef {
  asset_id: string;
  /** Local objectURL preview for a freshly-uploaded file (must be revoked). Empty for pre-existing refs. */
  previewUrl: string;
  /** true when previewUrl is a local objectURL that must be revoked. */
  local: boolean;
  /** Storage key for a pre-existing ref, resolved to a signed URL on render. null for local uploads. */
  storageKey: string | null;
}

/** Working-ref thumbnail: local uploads use their objectURL; pre-existing refs resolve a signed URL. */
function WorkingRefThumb({ r, isAudio }: { r: WorkingRef; isAudio: boolean }) {
  // Only pre-existing image refs need a presigned URL; skip for local uploads and audio.
  const { url: signed } = useAssetUrl(!r.local && !isAudio ? r.storageKey : null);
  const src = r.local ? r.previewUrl : signed;
  if (isAudio || !src) {
    return (
      <span className="flex h-full w-full items-center justify-center text-studio-faint">
        {isAudio ? <Music className="h-4 w-4" /> : <ImagePlus className="h-4 w-4" />}
      </span>
    );
  }
  return <img src={src} alt="" className="h-full w-full object-cover" />;
}

/** Same slot as the Gallery selection bar: above the mobile bottom nav, beside the desktop sidebar. */
const FLOATING_BAR =
  "fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom)+0.5rem)] left-1/2 z-30 w-[calc(100%-1rem)] -translate-x-1/2 justify-center sm:w-auto sm:max-w-[calc(100%-2rem)] lg:bottom-6 lg:left-[calc(50%+7.5rem)]";

export function ProductionAssetsPage() {
  const { mutate: globalMutate } = useSWRConfig();
  const [type, setType] = useState<ProductionAssetType>("character");
  const [showArchived, setShowArchived] = useState(false);

  const selectedProjectId = useProjectStore((s) => s.selectedProjectId);
  const setSelectedProject = useProjectStore((s) => s.setSelectedProject);
  const { data: projects } = useSWR<ProjectInfo[]>("/api/projects?include_archived=false", swrFetcher);
  // A project scope lists that project's elements plus the shared (unassigned) ones.
  const listKey = productionAssetsPath(type, selectedProjectId ?? undefined, showArchived);
  const { data: assets, isLoading } = useSWR<ProductionAssetInfo[]>(listKey, swrFetcher);
  const refreshElements = () => globalMutate(
    (key) => typeof key === "string" && /^\/api\/production-assets(?:\?|$)/.test(key),
    undefined,
    { revalidate: true },
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProductionAssetInfo | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [lastArchived, setLastArchived] = useState<ProductionAssetInfo | null>(null);

  const active = (assets ?? []).filter((a) => !a.archived);
  const archived = (assets ?? []).filter((a) => a.archived);

  const [selecting, setSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const movable = active.filter((a) => a.can_manage !== false);
  const selectedElements = movable.filter((a) => selectedIds.has(a.id));
  const stopSelecting = () => {
    setSelecting(false);
    setSelectedIds(new Set());
  };
  const projectMove = useProjectMove({
    move: moveProductionAssets,
    onMoved: () => {
      stopSelecting();
      return refreshElements();
    },
  });
  const openMove = (targets: ProductionAssetInfo[]) => {
    if (!targets.length) return;
    projectMove.dismiss();
    projectMove.open(targets.map((a) => a.id), { currentProjectId: sharedProjectId(targets) });
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (a: ProductionAssetInfo) => {
    setEditing(a);
    setDialogOpen(true);
  };

  const toggleArchive = async (a: ProductionAssetInfo) => {
    try {
      setBusyId(a.id);
      try {
        if (a.archived) {
          await unarchiveProductionAsset(a.id);
          setLastArchived(null);
        } else {
          await archiveProductionAsset(a.id);
          setLastArchived(a);
        }
        await refreshElements();
      } finally {
        setBusyId(null);
      }
    } catch (cause) {
      // Previously an unhandled rejection: the toggle silently did nothing.
      console.error(cause);
      window.alert(cause instanceof Error ? cause.message : "Update failed");
    }
  };

  const renderCard = (a: ProductionAssetInfo) => {
    const meta = TYPE_META[a.type];
    const Icon = meta.icon;
    const busy = busyId === a.id;
    const refs = a.canonical_references ?? [];
    const shown = refs.slice(0, 4);
    const overflow = refs.length - shown.length;

    const canMove = !a.archived && a.can_manage !== false;
    const checked = selectedIds.has(a.id);
    const ProjectIcon = a.project_id ? FolderKanban : Layers;

    return (
      <Card key={a.id} className={cn("flex flex-col p-4", a.archived && "opacity-60", selecting && checked && "ring-2 ring-studio-accent")}>
        <div className="flex items-start justify-between gap-3">
          {selecting && canMove && (
            <button
              type="button"
              onClick={() => setSelectedIds((ids) => toggleId(ids, a.id))}
              aria-pressed={checked}
              aria-label={`${checked ? "Deselect" : "Select"} ${a.name}`}
              className="-ml-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-studio-accent hover:bg-studio-card focus-visible:outline-none focus-visible:shadow-glow-sm"
            >
              {checked ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5 text-studio-muted" />}
            </button>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Icon className={cn("h-4 w-4 shrink-0", a.archived ? "text-studio-faint" : "text-studio-accent")} />
              <h3 className="truncate font-display font-semibold text-studio-ink">{a.name}</h3>
              {a.archived && <Badge variant="neutral">Archived</Badge>}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <Badge variant="accent">{a.type_label || meta.label}</Badge>
              <span className="font-mono text-[10px] text-studio-faint">@{a.slug}</span>
            </div>
            <p className="mt-1.5 flex min-w-0 items-center gap-1.5 text-[11px] text-studio-muted">
              <ProjectIcon className={cn("h-3.5 w-3.5 shrink-0", a.project_id ? "text-studio-accent" : "text-studio-faint")} />
              <span className="truncate">{a.project_id ? projectName(projects, a.project_id) : "Shared with all projects"}</span>
            </p>
          </div>
          <div className={cn("flex shrink-0 items-center gap-1", selecting && "hidden")}>
            {canMove && (
              <Button size="icon" variant="ghost" onClick={() => openMove([a])} title="Move to project" aria-label={`Move ${a.name} to project`}>
                <FolderInput className="h-4 w-4" />
              </Button>
            )}
            {!a.archived && (
              <Button size="icon" variant="ghost" onClick={() => openEdit(a)} title="Edit">
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              loading={busy}
              onClick={() => toggleArchive(a)}
              title={a.archived ? "Unarchive" : "Archive"}
            >
              {a.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {a.summary ? (
          <p className="mt-2.5 line-clamp-2 text-xs text-studio-muted">{a.summary}</p>
        ) : (
          <p className="mt-2.5 text-xs text-studio-faint">No summary</p>
        )}

        {shown.length > 0 && (
          <div className="mt-3 flex gap-2">
            {shown.map((r) => (
              <div
                key={r.asset_id}
                className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-studio-border bg-studio-card"
              >
                <RefThumb r={r} />
              </div>
            ))}
            {overflow > 0 && (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-dashed border-studio-border text-xs text-studio-muted">
                +{overflow}
              </div>
            )}
          </div>
        )}

        {a.generation_guidance && (
          <div className="mt-3 border-t border-studio-border pt-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-studio-faint">Guidance</p>
            <p className="mt-1 line-clamp-2 text-xs text-studio-muted">{a.generation_guidance}</p>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="page-container max-w-5xl">
      <div className="page-header sm:items-center">
        <div className="flex items-center gap-3">
          <Boxes className="h-5 w-5 text-studio-accent" />
          <h1 className="font-display text-xl font-bold tracking-tight">Elements</h1>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
          {movable.length > 0 && (
            <Button variant="secondary" className="w-full sm:w-auto" onClick={() => (selecting ? stopSelecting() : setSelecting(true))} aria-pressed={selecting}>
              {selecting ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
              {selecting ? "Done" : "Select"}
            </Button>
          )}
          <Button className={cn("w-full sm:w-auto", movable.length === 0 && "col-span-2")} onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New {TYPE_META[type].label.toLowerCase()}
          </Button>
        </div>
      </div>

      <div className="mb-5 flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center">
        <Tabs
          tabs={TYPE_TABS.map((t) => ({
            value: t.value,
            label: t.label,
            icon: <t.icon className="h-3.5 w-3.5" />,
          }))}
          value={type}
          onChange={setType}
        />
        <label className="flex min-h-11 cursor-pointer items-center justify-between gap-2 rounded-lg px-1 text-xs text-studio-muted sm:justify-start">
          Show archived
          <Switch checked={showArchived} onCheckedChange={setShowArchived} />
        </label>
      </div>

      {selectedProjectId && (
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-studio-border bg-studio-panel px-3 py-2 text-xs text-studio-muted">
          <FolderKanban className="h-3.5 w-3.5 shrink-0 text-studio-accent" />
          <span className="min-w-0 flex-1">
            Showing <span className="font-medium text-studio-ink">{projectName(projects, selectedProjectId)}</span> and shared elements
          </span>
          <Button size="sm" variant="ghost" onClick={() => setSelectedProject(null)}>View all projects</Button>
        </div>
      )}

      {lastArchived && <div role="status" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-studio-border bg-studio-panel p-3 text-sm">
        <span>{lastArchived.name} was archived. Its images are kept; enable Show archived to find it.</span>
        <Button size="sm" disabled={busyId !== null} onClick={() => void toggleArchive({ ...lastArchived, archived: true })}>Undo archive</Button>
        <Button size="sm" variant="ghost" onClick={() => setShowArchived(true)}>View archived</Button>
      </div>}
      {isLoading && !assets ? (
        <p className="text-sm text-studio-muted">Loading elements…</p>
      ) : (
        <div className="space-y-8">
          <section>
            {active.length === 0 ? (
              <EmptyState
                icon={Boxes}
                size="md"
                title={`No ${TYPE_META[type].label.toLowerCase()} elements yet`}
                hint={`Create one to reference it from any prompt via @${TYPE_META[type].label.toLowerCase()}-name`}
              />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">{active.map(renderCard)}</div>
            )}
          </section>

          {showArchived && archived.length > 0 && (
            <section>
              <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-studio-muted">
                Archived ({archived.length})
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">{archived.map(renderCard)}</div>
            </section>
          )}
        </div>
      )}

      {selecting ? (
        <SelectionToolbar
          label="Selected elements"
          count={selectedElements.length}
          selectableCount={movable.length}
          onSelectAll={() => setSelectedIds(new Set(movable.map((a) => a.id)))}
          onClear={stopSelecting}
          onMove={() => openMove(selectedElements)}
          busy={projectMove.busy}
          className={FLOATING_BAR}
        />
      ) : projectMove.outcome ? (
        <MoveResultBar
          outcome={projectMove.outcome}
          busy={projectMove.busy}
          onUndo={() => void projectMove.undo()}
          onDismiss={projectMove.dismiss}
          className={FLOATING_BAR}
        />
      ) : null}

      <MoveToProjectDialog
        open={projectMove.request !== null}
        count={projectMove.request?.ids.length ?? 0}
        noun="element"
        currentProjectId={projectMove.request?.currentProjectId}
        noProjectHint="Shared with every project"
        busy={projectMove.busy}
        error={projectMove.error}
        onClose={projectMove.close}
        onConfirm={(projectId, name) => void projectMove.confirm(projectId, name)}
      />

      {dialogOpen && (
        <AssetDialog
          type={type}
          editing={editing}
          onClose={() => {
            setDialogOpen(false);
            void refreshElements();
          }}
          onSaved={() => {
            setDialogOpen(false);
            void refreshElements();
          }}
        />
      )}
    </div>
  );
}

// ---- Create / Edit dialog ----

function AssetDialog({
  type,
  editing,
  onClose,
  onSaved,
}: {
  type: ProductionAssetType;
  editing: ProductionAssetInfo | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const effectiveType = editing?.type ?? type;
  const isCharacter = effectiveType === "character";

  const [savedId, setSavedId] = useState<string | null>(editing?.id ?? null);
  const [name, setName] = useState(editing?.name ?? "");
  const [summary, setSummary] = useState(editing?.summary ?? "");
  const [guidance, setGuidance] = useState(editing?.generation_guidance ?? "");
  const [saving, setSaving] = useState(false);
  const [canonicalUploading, setCanonicalUploading] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const uploadingReferences = canonicalUploading || audioUploading;
  const [error, setError] = useState<string | null>(null);

  const seedRefs = (refs: ReferenceAssetInfo[] | undefined, ids: string[]): WorkingRef[] => {
    const byId = new Map((refs ?? []).map((r) => [r.asset_id, r] as const));
    // Prefer resolved refs (have a storage key for preview); fall back to id-only placeholders.
    const source = refs && refs.length ? refs.map((r) => r.asset_id) : ids;
    return source.map((id) => {
      const r = byId.get(id);
      return { asset_id: id, previewUrl: "", local: false, storageKey: r ? refKey(r) : null };
    });
  };

  const [canonical, setCanonical] = useState<WorkingRef[]>(() =>
    seedRefs(editing?.canonical_references, editing?.canonical_reference_ids ?? [])
  );
  const [audio, setAudio] = useState<WorkingRef[]>(() =>
    seedRefs(editing?.audio_references, editing?.audio_reference_ids ?? [])
  );

  const persist = async (id: string | null): Promise<ProductionAssetInfo> => {
    const trimmed = name.trim();
    const canonicalIds = canonical.map((r) => r.asset_id);
    const audioIds = audio.map((r) => r.asset_id);
    if (id) {
      return updateProductionAsset(id, {
        name: trimmed,
        summary: summary.trim() || undefined,
        generation_guidance: guidance.trim() || undefined,
        canonical_reference_ids: canonicalIds,
        ...(isCharacter ? { audio_reference_ids: audioIds } : {}),
      });
    }
    return createProductionAsset({
      type: effectiveType,
      name: trimmed,
      summary: summary.trim() || undefined,
      generation_guidance: guidance.trim() || undefined,
      canonical_reference_ids: canonicalIds.length ? canonicalIds : undefined,
      ...(isCharacter && audioIds.length ? { audio_reference_ids: audioIds } : {}),
    });
  };

  const handleSave = async () => {
    if (!name.trim() || uploadingReferences || saving) return;
    setSaving(true);
    setError(null);
    try {
      await persist(savedId);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save element");
    } finally {
      setSaving(false);
    }
  };

  /** Generating needs a persisted element, so an unsaved draft is created first. */
  const ensureElement = async (): Promise<string> => {
    if (savedId) return savedId;
    const created = await persist(null);
    setSavedId(created.id);
    return created.id;
  };

  const [attachedThisSession, setAttachedThisSession] = useState(false);
  const applyElement = (element: ProductionAssetInfo, assetId: string) => {
    setCanonical((current) => mergeAttachedReference(current, element, assetId));
    setAttachedThisSession(true);
  };

  return (
    <Dialog
      open
      onClose={() => { if (!uploadingReferences && !saving) onClose(); }}
      title={`${savedId ? "Edit" : "New"} ${TYPE_META[effectiveType].label.toLowerCase()}`}
    >
      <div className="space-y-4 overflow-y-auto p-4 sm:p-5">
        <Field label="Name" required>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Zehra"
          />
        </Field>
        <Field label="Summary" hint={<span className="ml-1 normal-case text-studio-faint">optional</span>}>
          <Textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="A short identity / description"
            rows={2}
          />
        </Field>
        <Field
          label="Generation guidance"
          hint={<span className="ml-1 normal-case text-studio-faint">optional</span>}
        >
          <Textarea
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            placeholder="How models should render this element (style, traits, constraints)…"
            rows={3}
          />
        </Field>

        <RefUploader
          label="Canonical references"
          accept="image/*"
          refs={canonical}
          onChange={setCanonical}
          onUploadingChange={setCanonicalUploading}
        />

        <PortraitGenerator
          elementType={effectiveType}
          elementId={savedId}
          canGenerate={!!name.trim() && !uploadingReferences && !saving}
          attachedIds={canonical.map((r) => r.asset_id)}
          ensureElement={ensureElement}
          onAttached={applyElement}
        />

        {isCharacter && (
          <RefUploader label="Audio references" accept="audio/*" refs={audio} onChange={setAudio} onUploadingChange={setAudioUploading} />
        )}

        {error && (
          <p className="rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{error}</p>
        )}

        {attachedThisSession && (
          <p className="text-xs text-studio-faint">
            Generated references you attached are already saved to this element — closing now keeps them.
          </p>
        )}

        <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
          <Button variant="ghost" onClick={onClose} disabled={uploadingReferences || saving}>
            {attachedThisSession ? "Close" : "Cancel"}
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!name.trim() || uploadingReferences}>
            {savedId ? "Save" : "Create"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---- AI portraits: prompt -> 3 candidates -> pick / vary / attach ----

interface PortraitRequest {
  prompt: string;
  parentAssetId?: string;
}

function CandidateThumb({ asset }: { asset: ReferenceAssetInfo }) {
  return (
    <AssetThumb
      storageKey={asset.thumbnail_storage_key ?? asset.storage_key}
      fallback={<span className="h-full w-full animate-pulse bg-studio-panel" />}
    />
  );
}

function CandidateLightbox({
  asset,
  onClose,
}: {
  asset: ReferenceAssetInfo | null;
  onClose: () => void;
}) {
  const { url } = useAssetUrl(asset?.storage_key ?? null);
  return <MediaLightbox open={!!asset} onClose={onClose} url={url} type="image" alt="Candidate" />;
}

function CandidateGrid({
  assets,
  selectedId,
  attachedIds,
  onSelect,
  onEnlarge,
}: {
  assets: ReferenceAssetInfo[];
  selectedId: string | null;
  attachedIds: string[];
  onSelect: (assetId: string) => void;
  onEnlarge: (asset: ReferenceAssetInfo) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {assets.map((asset) => {
        const isSelected = selectedId === asset.asset_id;
        const isAttached = attachedIds.includes(asset.asset_id);
        return (
          <div
            key={asset.asset_id}
            className={cn(
              "group relative aspect-square overflow-hidden rounded-lg border bg-studio-card transition-all",
              isSelected ? "border-studio-accent shadow-glow-sm" : "border-studio-border"
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(asset.asset_id)}
              aria-pressed={isSelected}
              aria-label="Select candidate"
              className="h-full w-full"
            >
              <CandidateThumb asset={asset} />
            </button>
            {isAttached && (
              <span className="absolute left-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-studio-accent text-studio-bg">
                <Check className="h-3 w-3" />
              </span>
            )}
            <button
              type="button"
              onClick={() => onEnlarge(asset)}
              aria-label="Enlarge candidate"
              className="touch-reveal absolute right-1 top-1 flex h-9 w-9 items-center justify-center rounded-full bg-studio-bg/90 text-studio-muted opacity-0 shadow-sm transition-opacity hover:text-studio-ink group-hover:opacity-100 sm:h-8 sm:w-8"
            >
              <Maximize2 className="h-3 w-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** One run = one prompt; a model that cannot batch answers it with several jobs. */
interface PortraitRun {
  key: string;
  prompt: string;
  parentAssetId: string | null;
  entries: ElementGenerationInfo[];
  assets: ReferenceAssetInfo[];
  pending: boolean;
  failure: string | null;
}

function groupRuns(entries: ElementGenerationInfo[]): PortraitRun[] {
  const runs = new Map<string, PortraitRun>();
  for (const entry of entries) {
    const key = `${entry.prompt}::${entry.parent_asset_id ?? ""}`;
    const run = runs.get(key) ?? {
      key,
      prompt: entry.prompt,
      parentAssetId: entry.parent_asset_id,
      entries: [],
      assets: [],
      pending: false,
      failure: null,
    };
    run.entries.push(entry);
    run.assets.push(...entry.assets);
    run.pending = run.pending || isPendingState(entry.state);
    if (!run.failure && (entry.state === "failed" || entry.state === "cancelled")) {
      run.failure = entry.error?.message ?? "This generation did not finish.";
    }
    runs.set(key, run);
  }
  return [...runs.values()];
}

function RunBlock({
  run,
  awaiting,
  selectedId,
  attachedIds,
  onSelect,
  onEnlarge,
  onRetry,
}: {
  run: PortraitRun;
  awaiting?: boolean;
  selectedId: string | null;
  attachedIds: string[];
  onSelect: (assetId: string) => void;
  onEnlarge: (asset: ReferenceAssetInfo) => void;
  onRetry?: () => void;
}) {
  return (
    <div className="space-y-2">
      {run.assets.length > 0 && (
        <CandidateGrid
          assets={run.assets}
          selectedId={selectedId}
          attachedIds={attachedIds}
          onSelect={onSelect}
          onEnlarge={onEnlarge}
        />
      )}
      {(run.pending || awaiting) && (
        <p className="flex items-center gap-2 text-xs text-studio-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Generating…
        </p>
      )}
      {run.failure && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-studio-danger/10 px-3 py-2">
          <p className="flex-1 text-xs text-studio-danger">{run.failure}</p>
          {onRetry && (
            <Button size="sm" variant="ghost" onClick={onRetry}>
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** Debounced cost estimate for a portrait plan, shared by the fresh-run and Variants forms. */
function usePortraitEstimate(
  modelKey: string,
  capabilityKey: string,
  plan: PortraitPlan | null
): number | undefined {
  const [estimate, setEstimate] = useState<number | undefined>();
  useEffect(() => {
    if (!modelKey || !plan) {
      setEstimate(undefined);
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      void apiFetch<{ estimated_usd: string }>(
        `/api/capabilities/${encodeURIComponent(modelKey)}/capabilities/${capabilityKey}/estimate`,
        { method: "POST", body: JSON.stringify({ params: plan.params }) }
      )
        .then((row) => {
          if (active) setEstimate(Number(row.estimated_usd) * plan.jobCount);
        })
        .catch(() => {
          if (active) setEstimate(undefined);
        });
    }, 180);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [modelKey, capabilityKey, plan]);
  return estimate;
}

function PortraitGenerator({
  elementType,
  elementId,
  canGenerate,
  attachedIds,
  ensureElement,
  onAttached,
}: {
  elementType: ProductionAssetType;
  elementId: string | null;
  canGenerate: boolean;
  attachedIds: string[];
  ensureElement: () => Promise<string>;
  onAttached: (element: ProductionAssetInfo, assetId: string) => void;
}) {
  const { mutate: globalMutate } = useSWRConfig();
  const { data: models } = useSWR<ModelInfo[]>("/api/capabilities?modality=image", swrFetcher);

  const imageModels = useMemo(() => {
    const rank = (model: ModelInfo) =>
      (model.compatibility?.max_outputs ?? 1) >= CANDIDATE_COUNT ? 0 : 1;
    return (models ?? [])
      .filter((model) => (model.capability_keys ?? []).includes("text_to_image"))
      .sort((a, b) => rank(a) - rank(b) || a.display_name.localeCompare(b.display_name));
  }, [models]);

  const [modelKey, setModelKey] = useState<string>("");
  useEffect(() => {
    if (!modelKey && imageModels.length) setModelKey(imageModels[0].key);
  }, [imageModels, modelKey]);

  const { data: capabilities } = useSWR<CapabilityInfo[]>(
    modelKey ? `/api/capabilities/${encodeURIComponent(modelKey)}/capabilities` : null,
    swrFetcher
  );
  const textCapability = (capabilities ?? []).find((c) => c.key === "text_to_image");
  const referenceCapability = (capabilities ?? []).find((c) => c.key === "image_to_image");

  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runIds, setRunIds] = useState<string[]>([]);
  const [lastRequest, setLastRequest] = useState<PortraitRequest | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [variantOpen, setVariantOpen] = useState(false);
  const [variantPrompt, setVariantPrompt] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [lightbox, setLightbox] = useState<ReferenceAssetInfo | null>(null);

  const generationsKey = elementId ? elementGenerationsPath(elementId) : null;
  const { data: generations } = useSWR<ElementGenerationInfo[]>(generationsKey, swrFetcher);

  const plan = useMemo(() => {
    if (!textCapability || !prompt.trim()) return null;
    try {
      return buildPortraitPlan(textCapability.ui_schema.params, { prompt: prompt.trim() });
    } catch {
      return null;
    }
  }, [textCapability, prompt]);
  const estimate = usePortraitEstimate(modelKey, "text_to_image", plan);

  const variantPlan = useMemo(() => {
    if (!referenceCapability || !selectedId || !variantPrompt.trim()) return null;
    try {
      return buildPortraitPlan(referenceCapability.ui_schema.params, {
        prompt: variantPrompt.trim(),
        parentAssetId: selectedId,
      });
    } catch {
      return null;
    }
  }, [referenceCapability, selectedId, variantPrompt]);
  const variantEstimate = usePortraitEstimate(modelKey, "image_to_image", variantPlan);

  // Two clicks in one tick share this key, so the second is replayed, not charged.
  const pendingKey = useRef<string | null>(null);

  const run = async (request: PortraitRequest) => {
    const key = pendingKey.current ?? crypto.randomUUID();
    pendingKey.current = key;
    setBusy(true);
    setError(null);
    try {
      const id = await ensureElement();
      const started = await startElementGeneration({
        prompt: request.prompt,
        idempotency_key: key,
        model_key: modelKey || undefined,
        element_type: elementType,
        production_asset_id: id,
        parent_asset_id: request.parentAssetId,
      });
      setLastRequest(request);
      setRunIds(started.jobs.map((job) => job.generation_id));
      setSelectedId(null);
      setVariantOpen(false);
      setVariantPrompt("");
      if (started.errors.length) setError(started.errors[0].message);
      await globalMutate(elementGenerationsPath(id));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not start the generation");
    } finally {
      pendingKey.current = null;
      setBusy(false);
    }
  };

  const attach = async () => {
    if (!elementId || !selectedId) return;
    setBusy(true);
    setError(null);
    try {
      onAttached(await attachGeneratedReference(elementId, selectedId), selectedId);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not attach this image");
    } finally {
      setBusy(false);
    }
  };

  const rows = generations ?? [];
  const current = groupRuns(rows.filter((row) => runIds.includes(row.id)));
  const history = groupRuns(rows.filter((row) => !runIds.includes(row.id)));
  const awaiting =
    runIds.length > 0 && current.reduce((total, run) => total + run.entries.length, 0) < runIds.length;
  const unfinished = awaiting || current.some((entry) => entry.pending);
  const promptReady = prompt.trim().length > 0;

  // A data-equal poll leaves `generations` untouched, so the tick is what re-arms.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!generationsKey || !unfinished) return;
    const timer = window.setTimeout(() => {
      void globalMutate(generationsKey);
      setTick((value) => value + 1);
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [generationsKey, unfinished, globalMutate, tick]);

  return (
    <div className="rounded-xl border border-studio-border bg-studio-card p-3.5">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-studio-muted">
        <Sparkles className="h-3.5 w-3.5 text-studio-accent" />
        Generate with AI
      </div>

      <div className="space-y-2.5">
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={`Describe this ${TYPE_META[elementType].label.toLowerCase()} — look, wardrobe, mood…`}
          rows={2}
        />
        <Select value={modelKey} onChange={(e) => setModelKey(e.target.value)}>
          {!modelKey && (
            <option value="">
              {imageModels.length === 0 ? "No image model available" : "Choose a model"}
            </option>
          )}
          {imageModels.map((model) => (
            <option key={model.key} value={model.key}>
              {model.display_name}
            </option>
          ))}
        </Select>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-studio-faint">
            {estimate != null
              ? `About ${formatUsd(estimate, { decimals: 3 })} for ${CANDIDATE_COUNT} images`
              : `${CANDIDATE_COUNT} candidates per run`}
          </p>
          <Button
            size="sm"
            loading={busy}
            disabled={!promptReady || !canGenerate || !modelKey}
            onClick={() => void run({ prompt: prompt.trim() })}
          >
            <Wand2 className="h-3.5 w-3.5" />
            Generate {CANDIDATE_COUNT}
          </Button>
        </div>

        {!canGenerate && (
          <p className="text-xs text-studio-faint">Name this element first.</p>
        )}
        {canGenerate && !elementId && (
          <p className="text-xs text-studio-faint">
            Generating saves this element first, so every candidate stays with it.
          </p>
        )}
        {error && <p className="text-xs text-studio-danger">{error}</p>}
      </div>

      {(current.length > 0 || awaiting) && (
        <div className="mt-3.5 space-y-3 border-t border-studio-border pt-3">
          {current.map((entry) => (
            <RunBlock
              key={entry.key}
              run={entry}
              awaiting={awaiting}
              selectedId={selectedId}
              attachedIds={attachedIds}
              onSelect={setSelectedId}
              onEnlarge={setLightbox}
              onRetry={lastRequest ? () => void run(lastRequest) : undefined}
            />
          ))}
          {current.length === 0 && awaiting && (
            <p className="flex items-center gap-2 text-xs text-studio-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Generating…
            </p>
          )}

          {selectedId && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  loading={busy}
                  disabled={attachedIds.includes(selectedId)}
                  onClick={() => void attach()}
                >
                  <Check className="h-3.5 w-3.5" />
                  {attachedIds.includes(selectedId) ? "Already a reference" : "Use as reference"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setVariantOpen((open) => !open)}>
                  <Sparkles className="h-3.5 w-3.5" />
                  Variants
                </Button>
              </div>
              {!attachedIds.includes(selectedId) && (
                <p className="text-xs text-studio-faint">
                  Saves to this element immediately — Cancel below will not undo it.
                </p>
              )}
              {variantOpen && (
                <div className="space-y-1.5">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input
                      value={variantPrompt}
                      onChange={(e) => setVariantPrompt(e.target.value)}
                      placeholder="Describe the change — e.g. same face, winter coat"
                    />
                    <Button
                      size="sm"
                      loading={busy}
                      disabled={!variantPrompt.trim()}
                      onClick={() =>
                        void run({ prompt: variantPrompt.trim(), parentAssetId: selectedId })
                      }
                    >
                      Generate {CANDIDATE_COUNT}
                    </Button>
                  </div>
                  <p className="text-xs text-studio-faint">
                    {variantEstimate != null
                      ? `About ${formatUsd(variantEstimate, { decimals: 3 })} for ${CANDIDATE_COUNT} images`
                      : `${CANDIDATE_COUNT} candidates per run`}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-3.5 border-t border-studio-border pt-3">
          <button
            type="button"
            onClick={() => setHistoryOpen((open) => !open)}
            className="flex w-full items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-studio-muted hover:text-studio-ink"
          >
            {historyOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            <History className="h-3.5 w-3.5" />
            Previously generated ({history.reduce((total, run) => total + run.assets.length, 0)})
          </button>
          {historyOpen && (
            <div className="mt-2.5 space-y-3">
              {history.map((entry) => (
                <div key={entry.key} className="space-y-1.5">
                  <p className="line-clamp-2 text-xs text-studio-faint">{entry.prompt}</p>
                  <RunBlock
                    run={entry}
                    selectedId={selectedId}
                    attachedIds={attachedIds}
                    onSelect={setSelectedId}
                    onEnlarge={setLightbox}
                    onRetry={() =>
                      void run({
                        prompt: entry.prompt,
                        parentAssetId: entry.parentAssetId ?? undefined,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <CandidateLightbox asset={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

// ---- Multi-upload grid bound to working refs (collects uploaded asset_ids) ----

function RefUploader({
  label,
  accept,
  refs,
  onChange,
  onUploadingChange,
}: {
  label: string;
  accept: string;
  refs: WorkingRef[];
  onChange: (refs: WorkingRef[]) => void;
  onUploadingChange: (uploading: boolean) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isAudio = accept.startsWith("audio");

  // Revoke any local objectURLs on unmount.
  const refsRef = useRef(refs);
  useEffect(() => {
    refsRef.current = refs;
  }, [refs]);
  useEffect(
    () => () => {
      refsRef.current.forEach((r) => r.local && r.previewUrl && URL.revokeObjectURL(r.previewUrl));
    },
    []
  );

  async function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    setError(null);
    setUploading(true);
    onUploadingChange(true);
    const added: WorkingRef[] = [];
    try {
      for (const file of Array.from(fileList)) {
        const url = URL.createObjectURL(file);
        try {
          const asset = await uploadAsset(file);
          added.push({ asset_id: asset.asset_id, previewUrl: url, local: true, storageKey: null });
        } catch (e) {
          URL.revokeObjectURL(url);
          setError(e instanceof Error ? e.message : "Upload failed");
          break;
        }
      }
      if (added.length) onChange([...refs, ...added]);
    } finally {
      setUploading(false);
      onUploadingChange(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function remove(assetId: string) {
    const target = refs.find((r) => r.asset_id === assetId);
    if (target?.local && target.previewUrl) URL.revokeObjectURL(target.previewUrl);
    onChange(refs.filter((r) => r.asset_id !== assetId));
  }

  return (
    <div>
      <label className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-studio-muted">
        {label}
        <span className="font-normal normal-case text-studio-faint">(optional)</span>
      </label>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {refs.length > 0 && (
        <div className="mb-2 grid grid-cols-3 gap-2 sm:grid-cols-5">
          {refs.map((r) => (
            <div
              key={r.asset_id}
              className="group relative aspect-square overflow-hidden rounded-lg border border-studio-border bg-studio-card"
            >
              <WorkingRefThumb r={r} isAudio={isAudio} />
              <button
                type="button"
                onClick={() => remove(r.asset_id)}
                aria-label="Remove"
                className="touch-reveal absolute right-1 top-1 flex h-9 w-9 items-center justify-center rounded-full bg-studio-bg/90 text-studio-muted opacity-0 shadow-sm transition-opacity hover:text-studio-ink group-hover:opacity-100 sm:h-8 sm:w-8"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (!uploading) handleFiles(e.dataTransfer.files);
        }}
        className="group flex w-full flex-col items-center gap-1.5 rounded-xl border border-dashed border-studio-border bg-studio-card px-4 py-5 text-center transition-all hover:border-studio-accent/50 hover:bg-studio-card-hover disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-studio-panel text-studio-muted transition-colors group-hover:text-studio-accent">
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isAudio ? (
            <Music className="h-4 w-4" />
          ) : (
            <ImagePlus className="h-4 w-4" />
          )}
        </span>
        <span className="text-xs font-medium text-studio-muted group-hover:text-studio-ink">
          {uploading ? "Uploading…" : `Drop ${isAudio ? "audio" : "images"} or click to upload`}
        </span>
      </button>

      {error && <p className="mt-1.5 text-xs text-studio-danger">{error}</p>}
    </div>
  );
}
